import { css, cx } from "@emotion/css";
import { mergeProps } from "react-aria";
import { Cell as AriaCell, CellProps } from "react-aria-components";

export type textAlignment = "left" | "right" | "center";
export interface Props extends CellProps {
  isAction?: boolean;
  className?: string;
  textAlignment?: textAlignment;
  displayRightBorder?: boolean;
  onClick?: () => void;
  "data-testid"?: string;
  isStretchedRowLink?: boolean;
}

export function Cell(props: Props) {
  const { isStretchedRowLink, ...cellProps } = props;
  const styles = getStyles({
    textAlignment: props.textAlignment || "left",
    displayRightBorder: props.displayRightBorder,
    isStretchedRowLink,
  });

  if (props.isAction) {
    return (
      <AriaCell
        data-action=""
        {...mergeProps(cellProps, {
          className: cx(styles.cell, styles.action, props.className),
        })}
      >
        {(checkboxCellRenderProps) => {
          return (
            <div className={styles.actionCellContent}>
              {typeof props.children === "function"
                ? props.children(checkboxCellRenderProps)
                : props.children}
            </div>
          );
        }}
      </AriaCell>
    );
  }

  if (props.onClick) {
    return (
      <AriaCell
        {...mergeProps(cellProps, {
          className: cx(styles.cell, styles.clickable, props.className),
        })}
      >
        {(checkboxCellRenderProps) => {
          return (
            <div
              className={styles.clickableCellContent}
              onClick={props.onClick}
              onKeyUp={props.onClick}
              role="button"
              tabIndex={0}
            >
              {typeof props.children === "function"
                ? props.children(checkboxCellRenderProps)
                : props.children}
            </div>
          );
        }}
      </AriaCell>
    );
  }

  return (
    <AriaCell
      {...mergeProps(cellProps, {
        className: css(styles.cell, props.className),
      })}
    />
  );
}

function getStyles({
  textAlignment,
  displayRightBorder,
  isStretchedRowLink,
}: {
  textAlignment: textAlignment;
  displayRightBorder?: boolean;
  isStretchedRowLink?: boolean;
}) {
  return {
    cell: css({
      paddingTop: "var(--space-4)",
      paddingBottom: `calc(var(--space-4) - 1px)`,
      paddingLeft: "var(--space-3)",
      paddingRight: "var(--space-3)",
      outline: "none",
      "&[data-focus-visible]": {
        boxShadow: "var(--focus-action)",
      },
      overflow: isStretchedRowLink ? "visible" : "hidden",
      textAlign: textAlignment,
      borderRight: displayRightBorder
        ? "1px solid var(--border-base)"
        : undefined,
    }),
    action: css({
      paddingTop: "var(--space-2)",
      paddingBottom: `calc(var(--space-2) - 1px)`,
      paddingLeft: "var(--space-1)",
      paddingRight: "var(--space-1)",
    }),
    actionCellContent: css({ margin: "auto", width: "fit-content" }),
    clickable: css({
      padding: 0,
    }),
    clickableCellContent: css({
      paddingTop: "var(--space-4)",
      paddingBottom: `calc(var(--space-4) - 1px)`,
      paddingLeft: "var(--space-3)",
      paddingRight: "var(--space-3)",
      "&:hover": {
        cursor: "pointer",
        background: "var(--background-selected-weak)",
      },
    }),
  };
}
